import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  Pressable,
  Linking,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Globe } from 'lucide-react-native';
import { SEOHead } from '@/components/SEOHead';
import { AppStoreBadge, GooglePlayBadge } from '@/components/StoreBadges';
import { getMobileOS, type MobileOS } from '@/utils/mobileOsDetection';
import { getApiUrl } from '@/lib/apiConfig';

const REDIRECT_URL = getApiUrl('redirect-to-appstore');
const WEBSITE_URL = 'https://hekaya.ma';

const openUrl = (url: string) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  Linking.openURL(url).catch(() => {});
};

type LandingScreenProps = {
  seoPath: string;
};

export function LandingScreen({ seoPath }: LandingScreenProps) {
  const { width } = useWindowDimensions();
  const [detectedOS, setDetectedOS] = useState<MobileOS>(null);

  useEffect(() => {
    setDetectedOS(getMobileOS());
  }, []);

  const isCompact = width < 480;

  const cta = useMemo(() => {
    if (detectedOS === 'android') {
      return <GooglePlayBadge onPress={() => openUrl(REDIRECT_URL)} />;
    }
    if (detectedOS === 'ios') {
      return <AppStoreBadge onPress={() => openUrl(REDIRECT_URL)} />;
    }
    return (
      <Pressable
        onPress={() => openUrl(WEBSITE_URL)}
        style={({ pressed }) => [styles.websiteButton, pressed && styles.pressed]}
        accessibilityRole="link"
        accessibilityLabel="Visiter le site hekaya.ma"
      >
        <Globe size={22} color="#ffffff" strokeWidth={2} />
        <Text style={styles.websiteButtonText}>Visiter hekaya.ma</Text>
      </Pressable>
    );
  }, [detectedOS]);

  return (
    <LinearGradient
      colors={['#1a1a1a', '#241826', '#3a1f52']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.gradient}
    >
      <SEOHead
        title="Télécharger Hekaya"
        description="Téléchargez l'application Hekaya sur l'App Store ou Google Play, ou accédez à la plateforme depuis votre navigateur."
        path={seoPath}
      />
      <SafeAreaView style={styles.safe}>
        <View style={[styles.card, isCompact && styles.cardCompact]}>
          <Image
            source={require('@/assets/images/logo-hk.webp')}
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel="Hekaya"
          />

          <Text style={styles.title}>Regardez Hekaya partout</Text>
          <Text style={styles.subtitle}>
            Des séries et dramas courts à emporter avec vous. Téléchargez
            l'application ou continuez depuis votre navigateur.
          </Text>

          <View style={styles.ctaWrapper}>{cta}</View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
  },
  safe: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 32,
  },
  cardCompact: {
    paddingHorizontal: 20,
  },
  logo: {
    width: 160,
    height: 56,
    marginBottom: 32,
  },
  title: {
    color: '#ffffff',
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 34,
  },
  subtitle: {
    color: '#c9c4d1',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginTop: 12,
    marginBottom: 36,
  },
  ctaWrapper: {
    alignItems: 'center',
  },
  websiteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#A855F7',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 28,
    minWidth: 220,
    justifyContent: 'center',
  },
  websiteButtonText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '600',
  },
  pressed: {
    opacity: 0.8,
  },
});
